import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore, Refresh } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../../use-stores";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import {
  defaultRedemptionAppliedFilters,
  redemptionIgnoresDate,
  RedemptionAppliedFilters,
  RedemptionSource,
} from "../../../../../../models/Dashboard/Redemption/RedemptionFilters";
import { RedemptionFilterInfo } from "../../../../../../models/Dashboard/Redemption/RedemptionSummary";
import { RedemptionDashboardStore } from "../../../../stores/dashboards/RedemptionDashboardStore";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { DateRangePickerNew } from "../../../../../../views/components/common/textFields/DatePickerRangeNew";
import { useDebounce } from "../../../../../../views/components/common/hooks/Debounce";
import RedemptionHint from "./RedemptionHint";
import { trackRedemptionAction } from "../../../../../../analytics/redemptionTracking";

const SEARCH_DEBOUNCE_MS = 400;

const SOURCE_TABS: Array<{ id: RedemptionSource; label: string }> = [
  { id: "individual", label: "Individual" },
  { id: "onelink", label: "Onelink" },
  { id: "all", label: "All" },
];

type DatePreset = "7d" | "30d" | "90d" | "fy";

const DATE_PRESETS: Array<{ id: DatePreset; label: string }> = [
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "fy", label: "FY" },
];

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const getIndianFyStart = (today: Dayjs): Dayjs => {
  const fyYear = today.month() >= 3 ? today.year() : today.year() - 1;
  return dayjs(`${fyYear}-04-01`).startOf("day");
};

const getPresetRange = (preset: DatePreset): { start: Dayjs; end: Dayjs } => {
  const end = dayjs().endOf("day");
  if (preset === "7d") {
    return { start: dayjs().subtract(6, "day").startOf("day"), end };
  }
  if (preset === "90d") {
    return { start: dayjs().subtract(89, "day").startOf("day"), end };
  }
  if (preset === "fy") {
    return { start: getIndianFyStart(dayjs()), end };
  }
  return { start: dayjs().subtract(29, "day").startOf("day"), end };
};

const matchDatePreset = (start: Dayjs, end: Dayjs): DatePreset | null => {
  const presets: DatePreset[] = ["7d", "30d", "90d", "fy"];
  for (const preset of presets) {
    const range = getPresetRange(preset);
    if (start.isSame(range.start, "day") && end.isSame(range.end, "day")) {
      return preset;
    }
  }
  return null;
};

const parseIdList = (raw: string | null): number[] => {
  if (!raw) {
    return [];
  }
  return raw
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);
};

const idsToFilterValues = (ids: number[]): FilterValue[] =>
  ids.map((id) => ({ id: String(id), value: String(id) }));

const filterValuesToIds = (items: FilterValue[]): number[] =>
  items
    .map((item) => Number(item.id))
    .filter((id) => Number.isInteger(id) && id > 0);

const parseSource = (raw: string | null): RedemptionSource => {
  if (raw === "onelink" || raw === "all") {
    return raw;
  }
  return "individual";
};

const parseLocalDay = (raw: string | null, fallback: Dayjs): Dayjs => {
  if (!raw) {
    return fallback;
  }
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.startOf("day") : fallback;
};

const parseSearchParamsToApplied = (
  searchParams: URLSearchParams,
): RedemptionAppliedFilters => {
  const defaults = defaultRedemptionAppliedFilters();
  return {
    start_date: parseLocalDay(
      searchParams.get("start_date"),
      defaults.start_date,
    ),
    end_date: searchParams.get("end_date")
      ? parseLocalDay(searchParams.get("end_date"), defaults.end_date).endOf(
          "day",
        )
      : defaults.end_date,
    order_id: parseIdList(searchParams.get("order_id")),
    customer_id: parseIdList(searchParams.get("customer_id")),
    source: parseSource(searchParams.get("source")),
  };
};

const appliedToSearchParams = (
  applied: RedemptionAppliedFilters,
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("start_date", applied.start_date.format("YYYY-MM-DD"));
  params.set("end_date", applied.end_date.format("YYYY-MM-DD"));
  params.set("source", applied.source);
  if (applied.order_id.length > 0) {
    params.set("order_id", applied.order_id.join(","));
  }
  if (applied.customer_id.length > 0) {
    params.set("customer_id", applied.customer_id.join(","));
  }
  return params;
};

const mergeSelectedIntoList = (
  list: FilterValue[],
  selected: FilterValue[],
): FilterValue[] => {
  const ids = new Set(list.map((item) => String(item.id)));
  const extras = selected.filter((item) => !ids.has(String(item.id)));
  return extras.length > 0 ? [...extras, ...list] : list;
};

const filterInfoByKeys = (
  items: RedemptionFilterInfo[] | undefined,
  keys: string[],
): string | null => {
  if (!items || items.length === 0) {
    return null;
  }
  const match = items.find((item) => keys.includes(item.key));
  return match?.info?.trim() || null;
};

const FilterField = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | null;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          color={theme.palette.customColors.greyTextColor}
        >
          {label}
        </CustomTypography>
        <RedemptionHint text={hint} fontSize={14} />
      </Box>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
};

interface RedemptionFiltersProps {
  store: RedemptionDashboardStore;
}

const RedemptionFilters: React.FC<RedemptionFiltersProps> = ({ store }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { orderStore, customerStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialApplied = useMemo(
    () => parseSearchParamsToApplied(searchParams),
    // Hydrate once from the incoming URL so shared links are not stripped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [draftStart, setDraftStart] = useState<Dayjs>(
    initialApplied.start_date,
  );
  const [draftEnd, setDraftEnd] = useState<Dayjs>(initialApplied.end_date);
  const [selectedOrderIds, setSelectedOrderIds] = useState<FilterValue[]>(
    idsToFilterValues(initialApplied.order_id),
  );
  const [selectedCustomers, setSelectedCustomers] = useState<FilterValue[]>(
    idsToFilterValues(initialApplied.customer_id),
  );
  const [orderSearchResults, setOrderSearchResults] = useState<FilterValue[]>(
    [],
  );
  const [customerSearchResults, setCustomerSearchResults] = useState<
    FilterValue[]
  >([]);
  const [orderQuery, setOrderQuery] = useState("");
  const [orderSearchLoading, setOrderSearchLoading] = useState(false);
  const orderSearchSeq = useRef(0);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const debouncedOrderQuery = useDebounce(orderQuery, SEARCH_DEBOUNCE_MS);
  const debouncedCustomerQuery = useDebounce(customerQuery, SEARCH_DEBOUNCE_MS);

  const activePreset = matchDatePreset(draftStart, draftEnd);
  const displayedError = inlineError || store.summaryError;
  const filterInfo = store.summaryResult?.meta.filter_info;
  const dateHint = filterInfoByKeys(filterInfo, [
    "date",
    "date_range",
    "start_date",
  ]);
  const orderHint = filterInfoByKeys(filterInfo, ["order", "order_id"]);
  const customerHint = filterInfoByKeys(filterInfo, [
    "customer",
    "customer_id",
  ]);
  const sourceHint = filterInfoByKeys(filterInfo, ["source"]);
  const dateIgnored =
    selectedOrderIds.length > 0 || selectedCustomers.length > 0;

  const orderOptions = useMemo(
    () => mergeSelectedIntoList(orderSearchResults, selectedOrderIds),
    [orderSearchResults, selectedOrderIds],
  );
  const customerOptions = useMemo(
    () => mergeSelectedIntoList(customerSearchResults, selectedCustomers),
    [customerSearchResults, selectedCustomers],
  );

  const appliedUrlKey = [
    store.appliedFilters.start_date.format("YYYY-MM-DD"),
    store.appliedFilters.end_date.format("YYYY-MM-DD"),
    store.appliedFilters.order_id.join(","),
    store.appliedFilters.customer_id.join(","),
    store.appliedFilters.source,
  ].join("|");

  const applyFilters = useCallback(
    async (applied: RedemptionAppliedFilters) => {
      try {
        setInlineError(null);
        store.setAppliedFilters(applied);
        await store.refreshDashboard(applied);
      } catch (error) {
        setInlineError(
          store.summaryError ||
            getErrorMessage(error, "Failed to load product redemptions."),
        );
      }
    },
    [store],
  );

  const fetchOrderIds = useCallback(
    async (searchText: string) => {
      // Drop responses from superseded searches so a slow older request
      // cannot overwrite newer results or clear the loading state early.
      const seq = ++orderSearchSeq.current;
      setOrderSearchLoading(true);
      try {
        await orderStore.searchOrderIds(searchText);
        if (seq !== orderSearchSeq.current) {
          return;
        }
        setOrderSearchResults(
          (orderStore.orderIds ?? []).map((id) => ({
            id: String(id),
            value: String(id),
          })),
        );
      } catch (error) {
        if (seq !== orderSearchSeq.current) {
          return;
        }
        setOrderSearchResults([]);
        setInlineError(getErrorMessage(error, "Failed to search order IDs."));
      } finally {
        if (seq === orderSearchSeq.current) {
          setOrderSearchLoading(false);
        }
      }
    },
    [orderStore],
  );

  const fetchCustomers = useCallback(
    async (searchText: string) => {
      try {
        await customerStore.searchCustomers({
          page_no: 1,
          limit: 20,
          search_text: searchText,
        });
        setCustomerSearchResults(
          (customerStore.customers ?? [])
            .filter((customer) => customer.id != null)
            .map((customer) => ({
              id: String(customer.id),
              value: customer.name || String(customer.id),
            })),
        );
      } catch (error) {
        setCustomerSearchResults([]);
        setInlineError(getErrorMessage(error, "Failed to search customers."));
      }
    },
    [customerStore],
  );

  useEffect(() => {
    const text = debouncedOrderQuery.trim();
    if (!text) {
      orderSearchSeq.current += 1;
      setOrderSearchLoading(false);
      setOrderSearchResults([]);
      return;
    }
    void fetchOrderIds(text);
  }, [debouncedOrderQuery, fetchOrderIds]);

  useEffect(() => {
    if (!customerSearchOpen) {
      return;
    }
    void fetchCustomers(debouncedCustomerQuery.trim());
  }, [customerSearchOpen, debouncedCustomerQuery, fetchCustomers]);

  useEffect(() => {
    const init = async () => {
      try {
        setInlineError(null);
        store.setAppliedFilters(initialApplied);
        trackRedemptionAction("dashboard_view", initialApplied);
        await store.refreshDashboard(initialApplied);
      } catch (error) {
        setInlineError(
          store.summaryError ||
            getErrorMessage(error, "Failed to load product redemptions."),
        );
      }
    };
    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    try {
      setSearchParams(appliedToSearchParams(store.appliedFilters), {
        replace: true,
      });
    } catch (error) {
      setInlineError(
        getErrorMessage(error, "Failed to update the filter URL."),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedUrlKey, setSearchParams]);

  const buildAppliedFromDraft = (): RedemptionAppliedFilters => ({
    start_date: draftStart.startOf("day"),
    end_date: draftEnd.endOf("day"),
    order_id: filterValuesToIds(selectedOrderIds),
    customer_id: filterValuesToIds(selectedCustomers),
    source: store.appliedFilters.source,
  });

  const handleApply = () => {
    try {
      if (
        !redemptionIgnoresDate({
          order_id: filterValuesToIds(selectedOrderIds),
          customer_id: filterValuesToIds(selectedCustomers),
        }) &&
        draftStart.isAfter(draftEnd, "day")
      ) {
        setInlineError("Start date must be on or before the end date.");
        return;
      }
      const applied = buildAppliedFromDraft();
      trackRedemptionAction("filter_apply", applied);
      void applyFilters(applied);
    } catch (error) {
      setInlineError(getErrorMessage(error, "Failed to apply filters."));
    }
  };

  const handleClear = () => {
    try {
      const defaults = defaultRedemptionAppliedFilters();
      const cleared: RedemptionAppliedFilters = {
        ...defaults,
        source: store.appliedFilters.source,
      };
      setDraftStart(cleared.start_date);
      setDraftEnd(cleared.end_date);
      setSelectedOrderIds([]);
      setSelectedCustomers([]);
      setInlineError(null);
      trackRedemptionAction("filter_clear", cleared);
      void applyFilters(cleared);
    } catch (error) {
      setInlineError(getErrorMessage(error, "Failed to reset filters."));
    }
  };

  const handlePresetClick = (preset: DatePreset) => {
    try {
      const range = getPresetRange(preset);
      setDraftStart(range.start);
      setDraftEnd(range.end);
      trackRedemptionAction("date_preset_select", store.appliedFilters, {
        preset,
        context: {
          preset,
          start_date: range.start.format("YYYY-MM-DD"),
          end_date: range.end.format("YYYY-MM-DD"),
        },
      });
    } catch (error) {
      setInlineError(getErrorMessage(error, "Failed to set the date preset."));
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    try {
      if (range.startDate && range.endDate) {
        const start = range.startDate.startOf("day");
        const end = range.endDate.endOf("day");
        setDraftStart(start);
        setDraftEnd(end);
        trackRedemptionAction(
          "date_range_change",
          {
            ...store.appliedFilters,
            start_date: start,
            end_date: end,
          },
          {
            context: {
              start_date: start.format("YYYY-MM-DD"),
              end_date: end.format("YYYY-MM-DD"),
            },
          },
        );
      }
    } catch (error) {
      setInlineError(
        getErrorMessage(error, "Failed to set the custom date range."),
      );
    }
  };

  const handleRefresh = async () => {
    try {
      setInlineError(null);
      await store.refreshData();
    } catch (error) {
      setInlineError(
        store.summaryError ||
          getErrorMessage(error, "Failed to refresh product redemptions."),
      );
    }
  };

  const handleSource = (source: RedemptionSource) => {
    try {
      setInlineError(null);
      void store.changeSource(source);
    } catch (error) {
      setInlineError(
        getErrorMessage(error, "Failed to switch redemption source."),
      );
    }
  };

  return (
    <Paper
      sx={{
        mt: 3,
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        boxShadow:
          "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <CustomTypography variant="heading3" color={colors.darkBlueText}>
            Product redemptions
          </CustomTypography>
          <CustomTypography variant="body2" color={colors.greyTextColor}>
            Coupon journey for coupons created in this window, and the products
            those redemptions used.
          </CustomTypography>
        </Box>
        <AppButton
          label={store.summaryLoading ? "Refreshing..." : "Refresh"}
          variant="secondary"
          startIcon={<Refresh />}
          onClick={() => void handleRefresh()}
          disabled={store.summaryLoading}
          customStyles={{ minHeight: 36, height: 36, px: 2 }}
        />
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            width: "fit-content",
            p: 0.5,
            borderRadius: 2,
            border: `1px solid ${colors.lightGray}`,
            bgcolor: colors.white,
          }}
        >
          {SOURCE_TABS.map((tab) => {
            const selected = store.appliedFilters.source === tab.id;
            return (
              <AppButton
                key={tab.id}
                label={tab.label}
                variant={selected ? "primary" : "grayscale"}
                onClick={() => handleSource(tab.id)}
                customStyles={{ minHeight: 34, height: 34, px: 2 }}
              />
            );
          })}
        </Box>
        <RedemptionHint text={sourceHint} />
      </Box>

      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          borderRadius: 2,
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => {
            setIsSearchExpanded((prev) => {
              const next = !prev;
              trackRedemptionAction("search_panel_toggle", store.appliedFilters, {
                expanded: next,
                context: { expanded: next },
              });
              return next;
            });
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            cursor: "pointer",
          }}
        >
          <CustomTypography variant="heading6" fontWeight={600}>
            Search
          </CustomTypography>
          <IconButton
            size="small"
            aria-label={isSearchExpanded ? "Collapse search" : "Expand search"}
          >
            {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={isSearchExpanded}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Grid2 container spacing={2} columns={12}>
              <Grid2 size={{ xs: 12 }}>
                <FilterField label="Date range" hint={dateHint}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                      opacity: dateIgnored ? 0.45 : 1,
                      pointerEvents: dateIgnored ? "none" : "auto",
                    }}
                  >
                    {DATE_PRESETS.map((preset) => (
                      <AppButton
                        key={preset.id}
                        label={preset.label}
                        variant={
                          activePreset === preset.id ? "primary" : "grayscale"
                        }
                        onClick={() => handlePresetClick(preset.id)}
                        customStyles={{
                          minHeight: 32,
                          height: 32,
                          minWidth: "auto",
                          px: 1.5,
                        }}
                      />
                    ))}
                    <Box sx={{ flex: 1 }}>
                      <DateRangePickerNew
                        startDate={draftStart}
                        endDate={draftEnd}
                        onRangeSelect={handleRangeSelect}
                      />
                    </Box>
                  </Box>
                </FilterField>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <FilterField label="Order ID" hint={orderHint}>
                  <AppSearchDropdownMulti
                    title="Order ID"
                    list={orderOptions}
                    value={selectedOrderIds}
                    local={false}
                    floatingLabel
                    loading={
                      orderQuery !== debouncedOrderQuery || orderSearchLoading
                    }
                    handleSearch={(searchText) =>
                      setOrderQuery(searchText ?? "")
                    }
                    handleSelect={(items) => setSelectedOrderIds(items)}
                  />
                </FilterField>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <FilterField label="Customer" hint={customerHint}>
                  <AppSearchDropdownMulti
                    title="Customer"
                    list={customerOptions}
                    value={selectedCustomers}
                    local={false}
                    floatingLabel
                    handleSearch={(searchText) =>
                      setCustomerQuery(searchText ?? "")
                    }
                    handleSelect={(items) => setSelectedCustomers(items)}
                    onOpen={() => setCustomerSearchOpen(true)}
                  />
                </FilterField>
              </Grid2>
              <Grid2
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-end",
                  gap: 2,
                }}
              >
                <AppButton
                  variant="transparentPrimary"
                  label="Clear"
                  onClick={handleClear}
                  disabled={store.summaryLoading}
                  customStyles={{ width: "100%" }}
                />
                <AppButton
                  variant="primary"
                  label="Apply"
                  onClick={handleApply}
                  disabled={store.summaryLoading}
                  customStyles={{ width: "100%" }}
                />
              </Grid2>
            </Grid2>
          </Box>
        </Collapse>
      </Paper>

      {displayedError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {displayedError}
        </Alert>
      ) : null}
    </Paper>
  );
};

export default observer(RedemptionFilters);
